import { Chip, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';

type Props = {
  data: Record<string, unknown>;
};

const ASSET_URL_KEYS = new Set(['url', 'imageUrl']);

export function ResultTable({ data }: Props) {
  const entries = Object.entries(data).filter(([key]) => key !== 'ok');

  return (
    <TableContainer>
      <Table size="small">
        <TableBody>
          {entries.map(([key, value]) => (
            <TableRow key={key}>
              <TableCell sx={{ color: '#b08d57', fontFamily: 'monospace', border: 'none', py: 0.5, width: 180 }}>
                {key}
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace', border: 'none', py: 0.5 }}>
                <FormatValue fieldKey={key} value={value} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function isPreviewableUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/placeholders/');
}

function isVideoUrl(url: string): boolean {
  return url.endsWith('.mp4') || url.endsWith('.webm');
}

function AssetPreview({ url }: { url: string }) {
  if (isVideoUrl(url)) {
    return (
      <video
        src={url}
        controls
        muted
        loop
        style={{ maxWidth: 320, maxHeight: 180, display: 'block', marginTop: 4, borderRadius: 4, background: '#0d0f12' }}
      />
    );
  }
  return (
    <img
      src={url}
      alt="asset preview"
      style={{ maxWidth: 320, maxHeight: 180, display: 'block', marginTop: 4, borderRadius: 4, background: '#0d0f12' }}
    />
  );
}

function FormatValue({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  if (value === null || value === undefined) {
    return <span style={{ color: '#666' }}>—</span>;
  }

  if (typeof value === 'string') {
    const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      READY: 'success',
      SUCCEEDED: 'success',
      ACTIVE: 'success',
      RUNNING: 'info',
      ENQUEUED: 'info',
      PENDING: 'warning',
      CREATED: 'warning',
      FAILED: 'error',
      CLOSED: 'default',
      ABANDONED: 'default',
    };
    if (statusColors[value]) {
      return <Chip label={value} size="small" color={statusColors[value]} variant="outlined" />;
    }
    if (ASSET_URL_KEYS.has(fieldKey) && isPreviewableUrl(value)) {
      return (
        <>
          <span>{value}</span>
          <AssetPreview url={value} />
        </>
      );
    }
    return <>{value}</>;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return <>{String(value)}</>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: '#666' }}>[]</span>;
    return (
      <pre style={{ margin: 0, fontSize: '0.8rem', maxHeight: 200, overflow: 'auto' }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  if (typeof value === 'object') {
    return (
      <pre style={{ margin: 0, fontSize: '0.8rem', maxHeight: 200, overflow: 'auto' }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return <>{String(value)}</>;
}
